import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ChannelCheckupListResult,
  ChannelCheckupReport,
  channelCheckupApi,
} from "../../../../../api/channelCheckupApi";
import { ROUTES } from "../../../../../config/routes";
import { WidgetComponentProps, emailsInScope, getScope, matchesEmails } from "../../types";
import { useWidgetData } from "../../useWidgetData";
import {
  CardEmpty,
  CardError,
  CardLoading,
  Freshness,
  Kpi,
  KpiGrid,
  countTone,
  formatNumber,
} from "../../ui";
import { daysSince, isFuture, plural } from "./cardHelpers";

/** A channel with no public upload for this long is flagged on the card. */
export const NO_UPLOAD_DAYS = 30;

/** Mirrors `isPausedOrInactive` in views/ChannelCheckup/ChannelCheckup.tsx. */
const isPausedOrInactive = (r: ChannelCheckupReport): boolean => {
  if (r.inactive) return true;
  const op = (r.operationStatus || "").toLowerCase();
  return op === "paused" || op === "inactive";
};

/** Tracked channels whose last publish is missing or older than NO_UPLOAD_DAYS. */
const hasNoRecentUpload = (r: ChannelCheckupReport): boolean => {
  if (!r.lastUpdatesAvailable) return false;
  const days = daysSince(r.lastPublishedAt);
  return days === null || days > NO_UPLOAD_DAYS;
};

/** Same rule as the page's ScheduledDateCell: a schedule in the past counts as none. */
const hasNothingScheduled = (r: ChannelCheckupReport): boolean =>
  !isFuture(r.maxScheduledVideo) && !isFuture(r.maxScheduledShort);

const ChannelCheckupCard: React.FC<WidgetComponentProps> = ({ access, filters }) => {
  const { data, loading, error, reload } = useWidgetData<ChannelCheckupListResult>(
    "channel-checkup",
    () => channelCheckupApi.listChannels()
  );

  const includeInactive = filters.includeInactive === true;
  const scope = getScope(filters);
  const allowed = emailsInScope(filters, access.email);

  const stats = useMemo(() => {
    const scoped = (data?.reports ?? []).filter((r) =>
      matchesEmails(allowed, r.channelManagerEmails ?? [])
    );
    const inactive = scoped.filter(isPausedOrInactive).length;
    const visible = includeInactive ? scoped : scoped.filter((r) => !isPausedOrInactive(r));
    return {
      inScope: visible.length,
      inactive,
      noUpload: visible.filter(hasNoRecentUpload).length,
      nothingScheduled: visible.filter(hasNothingScheduled).length,
      qcIssues: visible.reduce((sum, r) => sum + (Number(r.qcOpenIssueCount) || 0), 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, includeInactive, JSON.stringify(allowed)]);

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  const updatedAt = data.syncStatus?.extras?.updatedAt ?? null;

  if (stats.inScope === 0 && stats.inactive === 0) {
    return (
      <>
        <CardEmpty
          icon="pi pi-inbox"
          title={scope === "mine" ? "No channels assigned to you" : "No channels in this view"}
          body={`${formatNumber(data.reports.length)} channels in Channel Checkup.`}
          action={
            <Link className="home-link" to={ROUTES.CHANNEL_CHECKUP}>
              Open Channel Checkup
            </Link>
          }
        />
        <Freshness label="Synced" at={updatedAt} />
      </>
    );
  }

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi
          label={scope === "mine" ? "Your channels" : "Channels"}
          value={stats.inScope}
          to={ROUTES.CHANNEL_CHECKUP}
          emphasis
          hint={includeInactive ? "Incl. paused / inactive" : "Active only"}
        />
        <Kpi
          label={`No upload in ${NO_UPLOAD_DAYS} days`}
          value={stats.noUpload}
          tone={countTone(stats.noUpload)}
          to={ROUTES.CHANNEL_CHECKUP}
        />
        <Kpi
          label="Nothing scheduled"
          value={stats.nothingScheduled}
          tone={countTone(stats.nothingScheduled)}
          hint="No future video or Short"
          to={ROUTES.CHANNEL_CHECKUP}
        />
      </KpiGrid>

      <div className="home-card__meta">
        <span className="home-muted">
          <i className="pi pi-pause" /> {formatNumber(stats.inactive)} paused / inactive
          {includeInactive ? "" : " (hidden)"}
        </span>
        {stats.qcIssues > 0 && (
          <Link className="home-link home-text-warn" to={ROUTES.METADATA_AUDIT_TITLE_QC}>
            <i className="pi pi-file-edit" /> {formatNumber(stats.qcIssues)} Title QC{" "}
            {plural(stats.qcIssues, "issue")}
          </Link>
        )}
        <Freshness label="Synced" at={updatedAt} />
      </div>
    </>
  );
};

export default ChannelCheckupCard;
